import { useState, type CSSProperties, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import AppShell from '../components/AppShell';
import { apiPost, errOf } from '../lib/api';

const inputStyle: CSSProperties = {
  width: '100%',
  borderRadius: 8,
  padding: '8px 12px',
  border: '1px solid var(--color-divider)',
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
};

/** 設密碼頁（用邀請連結 `?token=…`；接 `/api/auth/set-password`） */
export default function SetPasswordPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!token) {
    return (
      <AppShell title={t('setpw_title')}>
        <div className="flex items-center justify-center p-6">
          <div
            className="max-w-md w-full rounded-xl p-6"
            style={{ background: 'var(--color-card)', boxShadow: 'var(--shadow-soft)' }}
          >
            <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
              {t('setpw_nolink')}
            </p>
            <Link to="/" className="text-sm font-semibold" style={{ color: 'var(--color-primary)' }}>
              {t('back_home')}
            </Link>
          </div>
        </div>
      </AppShell>
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (pw1 !== pw2) {
      setError(t('setpw_mismatch'));
      return;
    }
    setBusy(true);
    setError('');
    const r = await apiPost('/api/auth/set-password', { token, password: pw1 });
    setBusy(false);
    if (r.ok) nav('/member');
    else setError(errOf(r.data) || t('setpw_failed'));
  }

  return (
    <AppShell title={t('setpw_title')}>
      <div className="flex items-center justify-center p-6">
        <form
          onSubmit={onSubmit}
          className="max-w-sm w-full rounded-xl p-6"
          style={{ background: 'var(--color-card)', boxShadow: 'var(--shadow-soft)' }}
        >
          <h1 className="text-xl font-bold mb-4">{t('setpw_title')}</h1>

          <label className="block text-sm mb-1" htmlFor="setpw-1" style={{ color: 'var(--color-text-secondary)' }}>
            {t('setpw_new')}
          </label>
          <input
            id="setpw-1"
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            value={pw1}
            onChange={(e) => setPw1(e.target.value)}
            className="mb-4"
            style={inputStyle}
          />

          <label className="block text-sm mb-1" htmlFor="setpw-2" style={{ color: 'var(--color-text-secondary)' }}>
            {t('setpw_confirm')}
          </label>
          <input
            id="setpw-2"
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            className="mb-4"
            style={inputStyle}
          />

          {error && (
            <p className="text-sm mb-3" style={{ color: 'var(--color-danger)' }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-lg px-4 py-2 font-semibold"
            style={{
              background: 'var(--color-primary)',
              color: 'var(--color-on-primary)',
              opacity: busy ? 0.6 : 1,
            }}
          >
            {busy ? t('setpw_working') : t('setpw_submit')}
          </button>
        </form>
      </div>
    </AppShell>
  );
}
